import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiDeleteOutline, mdiPlus } from "@mdi/js";
import {
  Button,
  Input,
  Switch,
  TextArea,
  TextField,
} from "@heroui/react";
import { ROUTES, discountUsagesPath } from "@/constants/routes";
import { discountFormStore } from "@/stores/DiscountFormStore";
import {
  RULE_TYPE_LABELS,
  RULE_TYPE_OPTIONS,
} from "./discount-format";
import "./DiscountsPage.css";

function RadioRow({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="dc-type-toggle" role="radiogroup" aria-label={name}>
      {options.map((opt) => (
        <label key={opt.value} className="dc-sc-item">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

const DiscountFormPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const store = discountFormStore;
  const isNew = !uuid;

  useEffect(() => {
    void store.loadServiceCenters();
    if (isNew) {
      store.resetForCreate();
    } else if (uuid) {
      void store.loadDiscount(uuid);
    }
  }, [uuid, isNew]);

  const fieldError = (field: string): string | undefined =>
    store.submitAttempted ? store.validationErrors[field] : undefined;

  if (!isNew && store.isLoading) {
    return (
      <div className="dc-form-page">
        <p>Загрузка…</p>
      </div>
    );
  }

  return (
    <div className="dc-form-page" data-testid="discount-form-page">
      <Button
        variant="ghost"
        className="dc-form-back"
        onPress={() => navigate(ROUTES.DISCOUNTS)}
      >
        <Icon path={mdiArrowLeft} size={0.8} />
        К списку
      </Button>

      <h1 className="dc-page__title">
        {isNew ? "Новая скидка" : "Редактирование скидки"}
      </h1>

      {!isNew && uuid ? (
        <Button
          variant="tertiary"
          onPress={() => navigate(discountUsagesPath(uuid))}
        >
          Применения
        </Button>
      ) : null}

      <section className="dc-section">
        <h2 className="dc-section__title">Основное</h2>
        <div>
          <span className="dc-section__title">Название</span>
          <TextField fullWidth isInvalid={Boolean(fieldError("title"))}>
            <Input
              value={store.title}
              onChange={(e) => store.setField("title", e.target.value)}
            />
          </TextField>
        </div>
        {fieldError("title") ? (
          <p className="dc-field-error">{fieldError("title")}</p>
        ) : null}
        <div>
          <span className="dc-section__title">Описание</span>
          <TextArea
            fullWidth
            rows={3}
            value={store.description}
            onChange={(e) => store.setField("description", e.target.value)}
          />
        </div>
        <label className="dc-sc-item">
          <Switch
            isSelected={store.isActive}
            onChange={(v) => store.setField("isActive", v)}
          >
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch>
          Активна
        </label>
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Распространение</h2>
        <RadioRow
          name="discount-type"
          value={store.type}
          options={[
            { value: "manual", label: "Клиентам" },
            { value: "promocode", label: "Промокод" },
          ]}
          onChange={(v) => store.setField("type", v)}
        />
        {store.type === "promocode" ? (
          <>
            <div>
              <span className="dc-section__title">Промокод</span>
              <TextField fullWidth isInvalid={Boolean(fieldError("promo_code"))}>
                <Input
                  value={store.promoCode}
                  onChange={(e) => store.setField("promoCode", e.target.value)}
                />
              </TextField>
            </div>
            {fieldError("promo_code") ? (
              <p className="dc-field-error">{fieldError("promo_code")}</p>
            ) : null}
          </>
        ) : null}
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Размер скидки</h2>
        <RadioRow
          name="value-type"
          value={store.valueType}
          options={[
            { value: "percent", label: "Процент" },
            { value: "fixed", label: "Фиксированная сумма" },
          ]}
          onChange={(v) => store.setField("valueType", v)}
        />
        <div>
          <span className="dc-section__title">
            {store.valueType === "percent" ? "Процент" : "Сумма, ₽"}
          </span>
          <TextField fullWidth isInvalid={Boolean(fieldError("value"))}>
            <Input
              value={store.valueInput}
              onChange={(e) => store.setField("valueInput", e.target.value)}
            />
          </TextField>
        </div>
        {fieldError("value") ? (
          <p className="dc-field-error">{fieldError("value")}</p>
        ) : null}
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Доли покрытия</h2>
        <div className="dc-split">
          <div className="dc-split__labels">
            <span>Платформа: {store.platformPercent}%</span>
            <span>СТО: {store.stoPercent}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={store.platformPercent}
            onChange={(e) =>
              store.setPlatformPercent(Number(e.target.value))
            }
          />
          <div className="dc-split__presets">
            <Button
              size="sm"
              variant="tertiary"
              onPress={() => store.setSplitPreset(0)}
            >
              0 / 100
            </Button>
            <Button
              size="sm"
              variant="tertiary"
              onPress={() => store.setSplitPreset(50)}
            >
              50 / 50
            </Button>
            <Button
              size="sm"
              variant="tertiary"
              onPress={() => store.setSplitPreset(100)}
            >
              100 / 0
            </Button>
          </div>
        </div>
        {fieldError("split") ? (
          <p className="dc-field-error">{fieldError("split")}</p>
        ) : null}
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Срок и лимиты</h2>
        <div className="dc-rule-row">
          <TextField fullWidth>
            <Input
              type="date"
              aria-label="Начало"
              value={store.startsAt}
              onChange={(e) => store.setField("startsAt", e.target.value)}
            />
          </TextField>
          <TextField fullWidth>
            <Input
              type="date"
              aria-label="Окончание"
              value={store.endsAt}
              onChange={(e) => store.setField("endsAt", e.target.value)}
            />
          </TextField>
        </div>
        <div className="dc-rule-row">
          <TextField fullWidth>
            <Input
              aria-label="Лимит применений (всего)"
              value={store.maxUsesTotal}
              onChange={(e) => store.setField("maxUsesTotal", e.target.value)}
            />
          </TextField>
          <TextField fullWidth>
            <Input
              aria-label="Лимит на клиента"
              value={store.maxUsesPerClient}
              onChange={(e) =>
                store.setField("maxUsesPerClient", e.target.value)
              }
            />
          </TextField>
        </div>
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Где действует</h2>
        <RadioRow
          name="scope"
          value={store.scope}
          options={[
            { value: "all_centers", label: "Все СТО" },
            { value: "selected_centers", label: "Выбранные СТО" },
          ]}
          onChange={(v) => store.setField("scope", v)}
        />
        {store.scope === "selected_centers" ? (
          <>
            <div className="dc-sc-list">
              {store.isServiceCentersLoading ? (
                <span>Загрузка СТО…</span>
              ) : (
                store.serviceCenterOptions.map((sc) => (
                  <label key={sc.uuid} className="dc-sc-item">
                    <input
                      type="checkbox"
                      checked={store.serviceCenterUuids.includes(sc.uuid)}
                      onChange={() => store.toggleServiceCenter(sc.uuid)}
                    />
                    {sc.name}
                  </label>
                ))
              )}
            </div>
            {fieldError("service_center_uuids") ? (
              <p className="dc-field-error">
                {fieldError("service_center_uuids")}
              </p>
            ) : null}
          </>
        ) : null}
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Кому</h2>
        <RadioRow
          name="audience"
          value={store.audience}
          options={[
            { value: "all", label: "Все клиенты" },
            { value: "selected_clients", label: "Выбранные клиенты" },
          ]}
          onChange={(v) => store.setField("audience", v)}
        />
        {store.audience === "selected_clients" ? (
          <div className="dc-stub">
            Поиск и выбор клиентов появятся в STO-227. Сейчас сохранение
            доступно только для аудитории «Все клиенты».
          </div>
        ) : null}
        {fieldError("audience") ? (
          <p className="dc-field-error">{fieldError("audience")}</p>
        ) : null}
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Правила (И)</h2>
        <div className="dc-rules">
          {store.rules.map((rule, index) => (
            <div key={rule.localId}>
              {index > 0 ? <div className="dc-rule-and">и</div> : null}
              <div className="dc-rule-row">
                <select
                  value={rule.rule_type}
                  onChange={(e) =>
                    store.updateRule(rule.localId, {
                      rule_type: e.target.value as typeof rule.rule_type,
                    })
                  }
                >
                  {RULE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {rule.rule_type === "min_order_sum" ? (
                  <TextField fullWidth>
                    <Input
                      aria-label="Мин. сумма, ₽"
                      value={rule.minSumRubles}
                      onChange={(e) =>
                        store.updateRule(rule.localId, {
                          minSumRubles: e.target.value,
                        })
                      }
                    />
                  </TextField>
                ) : null}
                {rule.rule_type === "specific_service" ? (
                  <TextField fullWidth>
                    <Input
                      aria-label="UUID услуги"
                      value={rule.serviceUuid}
                      onChange={(e) =>
                        store.updateRule(rule.localId, {
                          serviceUuid: e.target.value,
                        })
                      }
                    />
                  </TextField>
                ) : null}
                {rule.rule_type === "max_orders_count" ? (
                  <TextField fullWidth>
                    <Input
                      aria-label="Макс. заказов"
                      value={rule.maxOrders}
                      onChange={(e) =>
                        store.updateRule(rule.localId, {
                          maxOrders: e.target.value,
                        })
                      }
                    />
                  </TextField>
                ) : null}
                {rule.rule_type === "service_category" ? (
                  <TextField fullWidth>
                    <Input
                      aria-label="Категория"
                      value={rule.category}
                      onChange={(e) =>
                        store.updateRule(rule.localId, {
                          category: e.target.value,
                        })
                      }
                    />
                  </TextField>
                ) : null}
                {rule.rule_type === "first_order" ? (
                  <span>{RULE_TYPE_LABELS.first_order}</span>
                ) : null}
                <Button
                  isIconOnly
                  variant="tertiary"
                  aria-label="Удалить правило"
                  onPress={() => store.removeRule(rule.localId)}
                >
                  <Icon path={mdiDeleteOutline} size={0.85} />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="tertiary" onPress={() => store.addRule()}>
          <Icon path={mdiPlus} size={0.75} />
          Добавить правило
        </Button>
      </section>

      <section className="dc-section">
        <h2 className="dc-section__title">Push-уведомление</h2>
        <div className="dc-stub">
          {store.type === "promocode"
            ? "Отправка push для промокодов недоступна."
            : "Отправка push подключается в STO-228."}
        </div>
      </section>

      <div className="dc-form-actions">
        <Button variant="tertiary" onPress={() => navigate(ROUTES.DISCOUNTS)}>
          Отмена
        </Button>
        <Button
          variant="primary"
          isDisabled={store.isSubmitting}
          onPress={() => void store.submit(navigate)}
        >
          Сохранить
        </Button>
      </div>
    </div>
  );
});

DiscountFormPage.displayName = "DiscountFormPage";
export default DiscountFormPage;
